import { fc } from "@chatbotgang/etude/react/fc";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { EMPTY_STRING_PLACEHOLDER } from "@/appConstant";
import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import { NumberFormat } from "@/components/NumberFormat";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import { SurveyCakeTopUpButton } from "@/features/billing/SurveyCakeTopUpButton";
import { interlude } from "@/interlude";
import { ThresholdSettingModal } from "@/pages/billing/ThresholdSettingModal";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

export const BillingPage = fc(function BillingPage() {
  const { t, i18n } = useTranslation();

  const [alertSettingModalOpen, setAlertSettingModalOpen] = useState(false);

  const balanceQuery = interlude.balance.useGetBalance();

  const meQuery = interlude.auth.useGetMe();

  return (
    <PageLayout>
      <PageTitle>{t("billing.usage.title", "Billing & usage")}</PageTitle>
      <Card
        sx={{ display: "flex", alignItems: "center", width: "100%" }}
        variant="outlined"
      >
        <CardContent>
          <Stack direction="row" gap="48px" alignItems="flex-start">
            <Stack gap="8px">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="body">
                  {t("billing.availableBalance.title")}
                </Typography>
                <Tooltip
                  title={
                    <>
                      <div>{t("billing.availableBalance.tooltip.content")}</div>
                      <Button
                        sx={{ padding: "4px 0 0" }}
                        href={t("billing.availableBalance.tooltip.moreLink")}
                        variant="transparent"
                        endIcon={null}
                      >
                        {t("billing.availableBalance.tooltip.more")}
                      </Button>
                    </>
                  }
                  arrow
                >
                  <HelpOutlineIcon
                    fontSize="inherit"
                    sx={{ color: theme.colors.staticFgNote }}
                  />
                </Tooltip>
              </Stack>
              <Typography variant="value">
                {balanceQuery.isPending ? (
                  <CircularProgress />
                ) : (
                  <NumberFormat
                    nullishValue={EMPTY_STRING_PLACEHOLDER}
                    value={balanceQuery.data?.availableBalance}
                  />
                )}
              </Typography>
            </Stack>
            <Stack gap="8px">
              <Typography variant="body" color={theme.colors.staticFgNote}>
                {t("billing.actualBalance.title", "Actual balance")}
              </Typography>
              <Typography variant="h2">
                {balanceQuery.isPending ? (
                  <CircularProgress size={20} />
                ) : (
                  <NumberFormat
                    nullishValue={EMPTY_STRING_PLACEHOLDER}
                    value={balanceQuery.data?.actualBalance}
                  />
                )}
              </Typography>
            </Stack>
            <Stack gap="8px">
              <Typography variant="body" color={theme.colors.staticFgNote}>
                {t("billing.reservedBalance.title", "Reserved balance")}
              </Typography>
              <Typography variant="h2">
                {balanceQuery.isPending ? (
                  <CircularProgress size={20} />
                ) : (
                  <NumberFormat
                    nullishValue={EMPTY_STRING_PLACEHOLDER}
                    value={balanceQuery.data?.reservedBalance}
                  />
                )}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
        <CardContent sx={{ marginLeft: "auto", display: "flex", gap: "10px" }}>
          <Button
            variant="secondary"
            onClick={() => setAlertSettingModalOpen(true)}
            disabled={!balanceQuery.data}
          >
            {t("billing.alertSetting")}
          </Button>
          <SurveyCakeTopUpButton
            orgName={meQuery.data?.organization.name}
            orgUuid={meQuery.data?.organization.uuid}
            language={i18n.language}
          />
        </CardContent>
      </Card>
      {balanceQuery.data && balanceQuery.data.availableBalance <= 0 ? (
        <InfoBox
          variant="warning"
          description={t(
            "billing.availableBalance.emptyWarning",
            "Your available MAAC point balance is depleted. Top up to avoid service interruption.",
          )}
        />
      ) : null}
      <ThresholdSettingModal
        open={alertSettingModalOpen}
        closeModal={() => setAlertSettingModalOpen(false)}
      />
    </PageLayout>
  );
});
